import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiTrendingDown, FiClock, FiAlertTriangle, FiX } = FiIcons;

const ProblemSection = () => {
  const handleCTAClick = () => {
    window.open('https://app.noem.ai/cb/growthshop', '_blank');
  };

  const pains = [
    {
      icon: FiTrendingDown,
      title: "Cart Abandonment",
      stat: "67%",
      description: "of potential sales are lost at the last minute.",
      impact: "For a store doing $20,000/month, that's $40,000+ in lost revenue every single month.",
      color: "from-red-500 to-pink-500",
      bgColor: "from-red-50 to-pink-50"
    },
    {
      icon: FiClock,
      title: "24/7 Sales Gaps",
      stat: "24/7",
      description: "You can't be available around the clock to answer questions and close deals.",
      impact: "Night owls and international buyers are going to your competitors.",
      color: "from-orange-500 to-red-500",
      bgColor: "from-orange-50 to-red-50"
    },
    {
      icon: FiAlertTriangle,
      title: "Support Overload",
      stat: "80%",
      description: "Your team (or you) is swamped with repetitive questions about shipping, returns, and product details.",
      impact: "Wasting 20+ hours per week on the same 10 questions.",
      color: "from-yellow-500 to-orange-500",
      bgColor: "from-yellow-50 to-orange-50"
    }
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white opacity-50"></div>
      
      <div className="container mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
            Your Store Is{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-pink-500">
              Leaking Money.
            </span>{' '}
            Every Single Day.
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            You work hard to drive traffic to your store. You spend money on ads, create content, and build a brand. But the hard truth is, <span className="font-bold text-red-600">67% of the customers you attract will abandon their carts</span> before checking out.
          </p>

          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed mt-4">
            For a store doing $20,000 a month in sales, that's over <span className="font-bold text-red-600">$40,000 in lost revenue</span>. It's like filling a bucket with a giant hole in it. Every unanswered question, every moment of hesitation, every visitor who leaves without buying is a sale you'll never get back.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {pains.map((pain, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="relative group"
            >
              {/* Card Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${pain.bgColor} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
              
              <div className="relative bg-white rounded-3xl p-8 shadow-xl border border-gray-100 group-hover:shadow-2xl transition-all duration-300 h-full">
                {/* Icon */}
                <div className={`w-16 h-16 bg-gradient-to-br ${pain.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={pain.icon} className="text-white text-2xl" />
                </div>

                {/* Content */}
                <div className="mb-4">
                  <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${pain.color} text-white rounded-full px-4 py-2 font-black text-2xl mb-3`}>
                    {pain.stat}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {pain.title}
                  </h3>
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-4 font-medium">
                  {pain.description}
                </p>

                <div className="bg-gray-50 rounded-xl p-4 mt-4">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <span className="font-bold text-gray-900">The Impact:</span> {pain.impact}
                  </p>
                </div>

                {/* Hover Effect */}
                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${pain.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10`}></div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;